import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MosaicLens from "./MosaicLens";
import featurePages from "./pageData";
import featurePageExtras from "./extrasData";
import "./index.scss";

const pageKeysByPath = {
  "/cn/ad-management": "ad",
  "/cn/batch-builder": "batch",
  "/cn/creative-center": "creative",
};

const pageActionLinks = {
  ad: {
    trial: "https://xmp.mobvista.com/m/sign-up?landing_site=official_cn&referer=ad-management&lang=zh-CN",
    demo: "https://help-xmp.mobvista.com/docs/shi-pin-jiao-cheng?referer=ad-management#0d3072a6e890222808b37cdf726ca055",
  },
  batch: {
    trial: "https://xmp.mobvista.com/m/sign-up?landing_site=official_cn&referer=ad-creation&lang=zh-CN",
    demo: "https://help-xmp.mobvista.com/docs/shi-pin-jiao-cheng?referer=ad-creation#84bd7737cf152a0feb058c75ab41148a",
  },
  creative: {
    trial: "https://xmp.mobvista.com/m/sign-up?landing_site=official_cn&referer=creative-center&lang=zh-CN",
    demo: "https://help-xmp.mobvista.com/docs/shi-pin-jiao-cheng?referer=creative-center#d8d6d4e922e74199a66505c27dfd7ab9",
  },
};

const beforeItems = [
  "素材分散在本地和各个平台",
  "手动下载和整理素材文件",
  "难以追踪素材的投放表现",
  "无法快速识别优质素材",
  "重复上传相同素材浪费时间",
  "团队协作困难，素材难以共享",
];

const afterItems = [
  "云端统一存储，随时随地访问",
  "自动同步各平台素材，无需手动整理",
  "实时追踪每个素材的投放数据",
  "智能评分，精准识别优质创意",
  "一次上传，跨渠道共享，即取即用",
  "团队共享素材库，协作更高效",
];

const createAIQuestion = (title, description, prompt) => ({
  title,
  description,
  prompt,
  links: {
    chatgpt: `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
    perplexity: `https://www.perplexity.ai/search?q=${encodeURIComponent(prompt)}`,
  },
});

const aiQuestions = {
  ad: [
    createAIQuestion(
      "怎么用 XMP 整合多平台投放？",
      "Meta、Google、TikTok 一个后台管完。",
      "XMP 如何帮出海买量团队在一个后台同时管理 Meta、Google、TikTok 和 SDK 广告网络，减少跨平台切换？",
    ),
    createAIQuestion(
      "XMP 怎么打通跨渠道 ROI？",
      "投放、归因、变现数据统一对齐。",
      "XMP 如何打通投放、归因、变现数据，让跨渠道 ROI 在统一口径下真正可比？",
    ),
    createAIQuestion(
      "XMP 和现有工具怎么协同？",
      "无缝衔接 MMP、BI 工作流。",
      "XMP 如何与 AppsFlyer、Adjust、Singular 等 MMP 平台和团队现有 BI 工具协同工作？",
    ),
  ],
  batch: [
    createAIQuestion(
      "叉乘 vs 绑定，怎么选？",
      "什么测试用叉乘，什么用绑定。",
      "XMP 的叉乘组合和绑定组合，分别什么时候用？能不能各举一个具体的例子？",
    ),
    createAIQuestion(
      "5 国 × 10 素材怎么测？",
      "把多维测试拆成可执行的步骤。",
      "出海游戏团队同时测试 5 个国家、3 组定向、10 条素材，用 XMP 怎么设计批量搭建流程？",
    ),
    createAIQuestion(
      "广告模板怎么复用？",
      "提升广告创建标准化。",
      "如何通过 XMP 快速复用广告配置？",
    ),
  ],
  creative: [
    createAIQuestion(
      "怎么挑爆款素材？",
      "评分、ROAS、留存到底怎么看。",
      "怎么判断哪条素材是爆款？XMP 的评分、ROAS、留存等指标具体怎么看？",
    ),
    createAIQuestion(
      "XMP 怎么帮团队搭素材中心？",
      "从 0 到 1 的协作流程。",
      "出海广告团队第一次用 XMP 素材中心，应该怎么规划上传、打标签、智能评分、团队共享和复盘的流程？",
    ),
    createAIQuestion(
      "高 ROI 素材怎么复用？",
      "减少重复上传和整理。",
      "如何通过 XMP 快速复用历史高 ROI 的优质素材？",
    ),
  ],
};

const pageMetadata = {
  ad: {
    title: "广告管理 - XMP",
    description: "XMP 一站式整合多渠道广告、归因与变现数据，帮助出海团队统一管理广告并从 ROI 视角高效优化。",
    keywords: "XMP, 广告管理, 跨平台广告投放, ROI优化, 数据分析, 子渠道优化",
  },
  batch: {
    title: "批量搭建 - XMP",
    description: "使用 XMP 跨账户批量创建广告，通过叉乘、绑定、模板和自动化能力快速生成海量广告。",
    keywords: "XMP, 批量搭建, 批量创建广告, 跨账户投放, 广告模板, 自动创建广告",
  },
  creative: {
    title: "素材中心 - XMP",
    description: "XMP 素材中心统一管理、分析和复用广告素材，帮助出海团队持续沉淀高效创意资产。",
    keywords: "XMP, 素材中心, 素材管理, 素材分析, 素材评分, 广告创意",
  },
};

const getAsset = (path) => {
  const localPath = path.replace(/^assets\//, "");
  return require(`../../assets/images/FeatureLanding/${localPath}`);
};

function useFeaturePageMetadata(pageKey) {
  useEffect(() => {
    const metadata = pageMetadata[pageKey];
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    const description = document.head.querySelector('meta[name="description"]');
    const keywords = document.head.querySelector('meta[name="keywords"]');
    const previousDescription = description?.getAttribute("content") ?? null;
    const previousKeywords = keywords?.getAttribute("content") ?? null;

    document.title = metadata.title;
    document.documentElement.lang = "zh-CN";
    if (description) description.setAttribute("content", metadata.description);
    if (keywords) keywords.setAttribute("content", metadata.keywords);
    window.scrollTo(0, 0);

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
      if (description && previousDescription !== null) {
        description.setAttribute("content", previousDescription);
      }
      if (keywords && previousKeywords !== null) {
        keywords.setAttribute("content", previousKeywords);
      }
    };
  }, [pageKey]);
}

function FeatureSection({ feature, isExtra }) {
  const copy = (
    <div className="feature-landing-feature-copy">
      <h2>{feature.title}</h2>
      <p className="feature-landing-feature-body">{feature.body}</p>
      <ul className="feature-landing-feature-bullets">
        {feature.bullets.map((item) => (
          <li key={item}>
            <span className="feature-landing-bullet" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  const image = (
    <img
      className="feature-landing-feature-image"
      src={getAsset(feature.art)}
      width="690"
      height="611"
      loading="lazy"
      alt={`${feature.title}插画`}
    />
  );

  return (
    <section
      className={`feature-landing-feature${isExtra ? " is-extra" : ""}`}
      aria-label={feature.title}
    >
      {feature.reverse ? image : copy}
      {feature.reverse ? copy : image}
    </section>
  );
}

function ComparisonCard({ label, title, items, isAfter }) {
  return (
    <article className="feature-landing-comparison-card">
      <span className={`feature-landing-comparison-pill${isAfter ? " is-after" : ""}`}>
        {label}
      </span>
      <h3>{title}</h3>
      <ul>
        {items.map((item) => (
          <li key={item}>
            <span className={`feature-landing-comparison-icon${isAfter ? " is-after" : ""}`}>
              <img src={getAsset(isAfter ? "check.png" : "cross.png")} alt="" />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function CustomerLogoWall({ rows }) {
  return (
    <section className="feature-landing-customers" aria-labelledby="feature-customer-title">
      <div className="feature-landing-customers-inner">
        <h2 id="feature-customer-title">出海巨头都在用 XMP</h2>
        <div className="feature-landing-logo-wall" aria-label="XMP 客户 logo 墙">
          {rows.map((row, rowIndex) => {
            const repeatedItems = [...row.items, ...row.items];
            return (
              <div
                className={`feature-landing-logo-marquee${row.mode ? ` ${row.mode}` : ""}`}
                key={`${row.mode}-${rowIndex}`}
              >
                <div className="feature-landing-logo-track">
                  {repeatedItems.map(([src, alt], index) => (
                    <div className="feature-landing-logo-card" key={`${src}-${index}`}>
                      <img src={getAsset(src)} alt={alt} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeatureLanding() {
  const location = useLocation();
  const pageKey = pageKeysByPath[location.pathname] || "ad";
  const page = featurePages[pageKey];
  const extras = featurePageExtras[pageKey];
  const actionLinks = pageActionLinks[pageKey];
  const pageAIQuestions = aiQuestions[pageKey];
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const activeQuestion = pageAIQuestions[activeQuestionIndex];

  useFeaturePageMetadata(pageKey);

  useEffect(() => {
    setOpenFaqIndex(null);
    setActiveQuestionIndex(0);
  }, [pageKey]);

  return (
    <main className={`FeatureLandingPage FeatureLandingPage--${pageKey}`}>
      <section className="feature-landing-hero" aria-labelledby="feature-landing-title">
        <div className="feature-landing-hero-copy">
          <div className="feature-landing-hero-main">
            <span
              className="feature-landing-hero-eyebrow"
              style={{ backgroundColor: page.eyebrowColor }}
            >
              {page.eyebrow}
            </span>
            <h1 id="feature-landing-title">
              {page.heroLines.map((line) => <span key={line}>{line}</span>)}
            </h1>
            <p>{page.heroDescription}</p>
          </div>
          <div className="feature-landing-hero-actions">
            <a
              className="feature-landing-button is-primary"
              href={actionLinks.trial}
              target="_blank"
              rel="noopener noreferrer"
            >
              免费试用
            </a>
            <a
              className="feature-landing-button is-secondary"
              href={actionLinks.demo}
              target="_blank"
              rel="noopener noreferrer"
            >
              观看演示
            </a>
          </div>
        </div>
        <img
          className="feature-landing-hero-image"
          src={getAsset(page.heroArt)}
          width="840"
          height="930"
          alt={`${page.name}首屏插画`}
        />
      </section>

      <div className="feature-landing-features">
        {page.features.map((feature, index) => (
          <FeatureSection
            feature={feature}
            isExtra={pageKey === "creative" && index === 5}
            key={feature.title}
          />
        ))}
      </div>

      <section className="feature-landing-comparison" aria-labelledby="feature-comparison-title">
        <div className="feature-landing-comparison-heading">
          <h2 id="feature-comparison-title">使用 XMP 前后对比</h2>
          <p>素材管理时间节省 95%</p>
        </div>
        <div className="feature-landing-comparison-grid">
          <ComparisonCard
            label="使用前"
            title="传统素材管理方式"
            items={beforeItems}
            isAfter={false}
          />
          <ComparisonCard
            label="使用后"
            title="XMP 智能素材中心"
            items={afterItems}
            isAfter
          />
        </div>
      </section>

      <CustomerLogoWall rows={extras.logos} />

      <section className="feature-landing-cta">
        <div className="feature-landing-cta-card">
          <h2>和 XMP 一起高效出海</h2>
          <a href={actionLinks.trial} target="_blank" rel="noopener noreferrer">免费试用</a>
        </div>
      </section>

      <section className="feature-landing-faq" aria-labelledby="feature-faq-title">
        <h2 id="feature-faq-title">常见问题</h2>
        <div className="feature-landing-faq-list">
          {extras.faq.map(([question, answer], index) => {
            const isOpen = openFaqIndex === index;
            const answerId = `feature-faq-answer-${pageKey}-${index}`;
            return (
              <div className="feature-landing-faq-item" key={question}>
                <button
                  className="feature-landing-faq-row"
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                >
                  <span>{question}</span>
                  <span className="feature-landing-faq-plus" aria-hidden="true">+</span>
                </button>
                {isOpen && (
                  <div className="feature-landing-faq-answer" id={answerId}>
                    <p>{answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="feature-landing-ask" aria-labelledby="feature-ask-title">
        <div className="feature-landing-ask-mosaic" aria-hidden="true">
          <MosaicLens
            background="#FFFFFF"
            color1="#156EF8"
            color2="#FFE3A5"
            speed={20}
            size={180}
            angle={0}
            tileSize={6}
            hover={70}
            reach={220}
            style={{ minWidth: 0, minHeight: 0 }}
          />
        </div>
        <div className="feature-landing-ask-heading">
          <span className="feature-landing-ask-eyebrow">ASK AI</span>
          <h2 id="feature-ask-title">还有疑问？直接问 AI</h2>
          <p>点一下下面的问题，跳到 AI 工具继续问。</p>
        </div>
        <div className="feature-landing-ask-card">
          <div className="feature-landing-ask-options">
            {pageAIQuestions.map((question, index) => (
              <button
                className={`feature-landing-ask-option${activeQuestionIndex === index ? " is-selected" : ""}`}
                type="button"
                aria-pressed={activeQuestionIndex === index}
                onClick={() => setActiveQuestionIndex(index)}
                key={question.title}
              >
                <strong>{question.title}</strong>
                <span>{question.description}</span>
              </button>
            ))}
          </div>
          <div className="feature-landing-ask-current">
            <span>当前问题</span>
            <strong>{activeQuestion.prompt}</strong>
          </div>
          <div className="feature-landing-ask-actions">
            <a
              className="is-primary"
              href={activeQuestion.links.chatgpt}
              target="_blank"
              rel="noopener noreferrer"
            >
              ChatGPT ↗
            </a>
            <a
              href={activeQuestion.links.perplexity}
              target="_blank"
              rel="noopener noreferrer"
            >
              Perplexity ↗
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default FeatureLanding;
